/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { ViewModel } from "../Common/ViewModel";
import { CreateViewDialogComponent } from "./CreateViewDialogComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ErrorTracker } from "@adapptio/lib-components";

export interface CreateViewDialog_Params {
	basePath?: string;
}

export interface CreateViewDialog_Result {
	resourceId: string;
}

export interface ICreateViewDialog_CreateParams {
	basePath: string;
	name: string;
}

export class CreateViewDialog extends Dialog<CreateViewDialog_Params, CreateViewDialog_Result> {
	/** Services */
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;
	private toastSvc: ToastService;

	/** New resource ID */
	private resourceId: string;

	/** Document model */
	private viewModel: ViewModel;

	/** If the view is being saved */
	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: CreateViewDialog_Params) {
		super(dc, id, params);

		this.resourceSvc = this.dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.toastSvc = dc.get("toastService");

		this.resourceId = uuid_v4();

		this.viewModel = new ViewModel(this.runtimeSvc, {});
		this.viewModel.createDefault();

		const idModel = this.viewModel.getModel().props.id;
		idModel.schema.setValue(idModel, this.resourceId, false);
	}

	public getComponent(): typeof CreateViewDialogComponent {
		return CreateViewDialogComponent;
	}

	/**
	 * Returns view model
	 */
	public getViewModel(): ViewModel {
		return this.viewModel;
	}

	/**
	 * Creates a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async create(params: ICreateViewDialog_CreateParams): Promise<boolean> {
		if (this.saveInProgress) {
			return false;
		}

		const content = this.viewModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onSaveStateChange);

		try {
			await this.resourceSvc.createBlueprint(params.basePath, params.name, content, this.resourceId);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				// @todo Translate
				message: "View has been created.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				// @todo Translate
				message: "Failed to create view. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.saveInProgress = false;
			emitEvent(this.onSaveStateChange);
		}

		return true;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns if a resource with given baseName and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfBlueprintExists(basePath: string, name: string): boolean {
		return !!this.resourceSvc.getResourceByPath(
			normalizeResourcePath(basePath, name, { sanitize: true })
		);
	}
}
